import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { projectAPI } from '../../apis';
import { ProjectLog } from '../../types';

interface ProjectHistoryProps {
  projectId: string;
}

const ProjectHistory: React.FC<ProjectHistoryProps> = ({ projectId }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<ProjectLog[]>([]);
  const [summary, setSummary] = useState<{
    total_logs: number;
    project_logs: number;
    permit_logs: number;
    document_logs: number;
  } | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await projectAPI.getProjectLogs(projectId);
        
        if (response.status === 'success' && response.data) {
          setLogs(response.data.logs);
          setSummary(response.data.summary);
        } else {
          throw new Error(response.message || 'Failed to fetch project history');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred while fetching project history');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [projectId]);

  const getLogIcon = (type: string) => {
    switch (type) {
      case 'project':
        return <FolderIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
      case 'permit':
        return <AssignmentIcon fontSize="small" sx={{ color: theme.palette.info.main }} />;
      case 'document':
        return <DescriptionIcon fontSize="small" sx={{ color: theme.palette.success.main }} />;
      default:
        return null;
    }
  };

  const getLogTypeColor = (type: string) => {
    switch (type) {
      case 'project':
        return theme.palette.primary.main;
      case 'permit':
        return theme.palette.info.main;
      case 'document':
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius: '16px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, borderRadius: '16px' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: '16px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Project History
        </Typography>
        {summary && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              size="small"
              icon={<FolderIcon />}
              label={`${summary.project_logs} Project`}
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
            />
            <Chip 
              size="small"
              icon={<AssignmentIcon />}
              label={`${summary.permit_logs} Permits`}
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}
            />
            <Chip 
              size="small"
              icon={<DescriptionIcon />}
              label={`${summary.document_logs} Documents`}
              sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}
            />
          </Box>
        )}
      </Box>
      
      <List>
        {logs.map((log, index) => (
          <React.Fragment key={log.id}>
            <ListItem
              sx={{
                py: 2,
                px: 0,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 2 }}>
                {getLogIcon(log.type)}
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.primary">
                        {log.description}
                      </Typography>
                      <Chip
                        label={log.type}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '0.75rem',
                          bgcolor: alpha(getLogTypeColor(log.type), 0.1),
                          color: getLogTypeColor(log.type),
                        }}
                      />
                    </Box>
                  }
                  secondary={log.timestamp ? format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm') : 'N/A'}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: 'text.secondary',
                  }}
                />
              </Box>
            </ListItem>
            {index < logs.length - 1 && <Divider />}
          </React.Fragment>
        ))}
        {logs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No history available
            </Typography>
          </Box>
        )}
      </List>
    </Paper>
  );
};

export default ProjectHistory; 